import { getOrMakeUnitDefinitionFromMdsSiMarkup } from '@metservicedev/cf-units';
import React from 'react';
import { useMDSContext } from '@metservice/use-mds';

import aggregateFunctions from '../../../utils/named-aggregate-functions';
import LatexElement from '../../../utils/latexElement';
import DataTableThreatLegend from '../DataTableThreatLegend';
import { DataTableDiv, ThreatCell } from '../DataTable.style';
import { DataTableProps } from '../types';
import { filterDatasetsAndCapabilities, getCellThreatLimit, getCellText, getDisplayValue, getNumberOfDatumsInDay } from './utils';

const DataTableHorizontal = ({
    title,
    datasets,
    reportTime,
    databilities,
    arrayOfTimes,
    headings,
    fontSize,
    showGroupLabels,
    binning,
    bins,
    rotateVerticalLabels,
    threatLimits,
    limits,
    offsetLeft,
    useFixedOffset,
    displayTableHeading,
    headerPadding,
}: DataTableProps) => {
    const maxArrayOfTimesLength = 72; // 24 values per day, three days
    const truncatedArrayOfTimes = arrayOfTimes.length > maxArrayOfTimesLength;
    const { capabilities } = useMDSContext();

    arrayOfTimes = arrayOfTimes.slice(0, maxArrayOfTimesLength);

    return (
        <DataTableDiv
            className='dataTableRender horizontal-dataTable'
            headerPadding={headerPadding}
            fontSize={fontSize}
            setMaxWidthFirstColumn={useFixedOffset}
            maxWidthFirstColumn={offsetLeft}
        >
            {truncatedArrayOfTimes && <div>Too much data to display, data has been truncated</div>}
            <div className='tableWrapper'>
                <table cellSpacing={0} cellPadding={0}>
                    {displayTableHeading && (
                        <thead>
                            <tr className='heading'>
                                <th colSpan={showGroupLabels ? 5 : 4} className='bordered firstColumn'>
                                    {title}
                                </th>
                                {arrayOfTimes.map((time, key) => {
                                    const isStartOfDay = key === 0 || time.dayOfYear() !== arrayOfTimes[key - 1].dayOfYear();

                                    if (!isStartOfDay) {
                                        return;
                                    }

                                    const numberOfDatumsInDay = getNumberOfDatumsInDay(binning, bins, time, arrayOfTimes);
                                    let addedClass = 'bordered heading endOfDayRow';

                                    if (Math.ceil((key + 1) / numberOfDatumsInDay) % 2 === 1) {
                                        addedClass += ' oddDay';
                                    }

                                    return (
                                        <th key={time.unix() + 'time label day'} className={addedClass} colSpan={numberOfDatumsInDay}>
                                            <div>{time.format('ddd')}</div>
                                            <div>{time.format('DD MMM')}</div>
                                        </th>
                                    );
                                })}
                            </tr>
                            <tr className='heading'>
                                <th colSpan={showGroupLabels ? 5 : 4} className='bordered firstColumn'>
                                    {`${reportTime?.format('z')} (UTC ${reportTime?.format('Z')})`}
                                </th>
                                {arrayOfTimes.map((time, key) => {
                                    const numberOfDatumsInDay = getNumberOfDatumsInDay(binning, bins, time, arrayOfTimes);
                                    let addedClass = 'bordered heading';

                                    if (Math.ceil((key + 1) / numberOfDatumsInDay) % 2 === 1) {
                                        addedClass += ' oddDay';
                                    }

                                    return (
                                        <th key={time.unix() + 'time label hm'} className={addedClass}>
                                            <span className={rotateVerticalLabels ? 'rotate-heading' : undefined}>{time.format(binning ? 'HH' : 'HH:mm')}</span>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                    )}
                    {headings.length && (
                        <tbody>
                            {headings.map((heading) => {
                                return heading.databilities.map((datability, i) => {
                                    const lastDatabilityRow = i == heading.databilities.length - 1;
                                    const headingLabelTd = !(i == 0 && showGroupLabels) ? null : (
                                        <td
                                            key={`label-${heading.id}-${i}`}
                                            colSpan={2}
                                            rowSpan={heading.databilities.length}
                                            className='bordered heading-label'
                                        >
                                            <div className={rotateVerticalLabels ? 'rotate-this-outer' : undefined}>
                                                <div className={rotateVerticalLabels ? 'rotate-this' : undefined}>{heading.label}</div>
                                            </div>
                                        </td>
                                    );

                                    const { wholeDataset, wholeCapability, capUnit } = filterDatasetsAndCapabilities({
                                        databilities,
                                        databilityId: datability,
                                        datasets,
                                    });
                                    const capabilityUnitDefinition = capUnit && getOrMakeUnitDefinitionFromMdsSiMarkup(capUnit);
                                    const displayAsSiUnit =
                                        (heading.databilityUnits && heading.databilityUnits[i]) || (capabilityUnitDefinition && capabilityUnitDefinition.unit);
                                    const latexUnit = (displayAsSiUnit && getOrMakeUnitDefinitionFromMdsSiMarkup(displayAsSiUnit).niceLatex) || '';

                                    const reportTZ = reportTime?.tz();
                                    const binFunction = binning ? aggregateFunctions[heading.aggregateFunctions[i] || 'first'].f : (x: number[]) => x;
                                    const customUnitLabel = (heading.databilityUnitLabels && heading.databilityUnitLabels[i]) || heading.databilityUnits[i];
                                    const unitLabel = customUnitLabel === heading.databilityUnits[i] ? LatexElement(latexUnit) : LatexElement(customUnitLabel);

                                    return (
                                        <tr key={`heading-${heading.id}-${i}`}>
                                            {headingLabelTd}
                                            <td colSpan={showGroupLabels ? 3 : 4} className='bordered unit-label firstColumn'>
                                                <span>
                                                    {(heading.databilityLabels && heading.databilityLabels[i]) ||
                                                        (wholeCapability && wholeCapability.display_name_short) ||
                                                        ''}{' '}
                                                    {!heading.hideUnits[i] && unitLabel}
                                                </span>
                                            </td>
                                            {arrayOfTimes.map((time, key) => {
                                                let addedClass = '';

                                                if (lastDatabilityRow) {
                                                    addedClass += ' endOfGroupRow';
                                                }

                                                if (key == arrayOfTimes.length - 1) {
                                                    addedClass += ' lastElementInColumn';
                                                }

                                                const numberOfDatumsInDay = getNumberOfDatumsInDay(binning, bins, time, arrayOfTimes);

                                                if (Math.ceil((key + 1) / numberOfDatumsInDay) % 2 === 1) {
                                                    addedClass += ' oddDay';
                                                }

                                                const displayValue = getDisplayValue({
                                                    time,
                                                    fromIndex: null,
                                                    reportTZ,
                                                    wholeDataset,
                                                    wholeCapability,
                                                    binning,
                                                    bins,
                                                    dp: heading.dp[i],
                                                    direction: heading.directions[i],
                                                    binFunction,
                                                    unit: displayAsSiUnit,
                                                    directionalVisualization: heading.directionalVisualization[i],
                                                    vectorNormalization: heading.vectorNormalization[i],
                                                    capabilities,
                                                });

                                                const cellThreatLimit = getCellThreatLimit({
                                                    heading: heading,
                                                    index: i,
                                                    threatLimits,
                                                    limits,
                                                    value: displayValue.displayValue,
                                                });

                                                const cellText = getCellText({ cellThreatLimit, displayValue });
                                                const colour = heading.colours[i];

                                                return (
                                                    <ThreatCell
                                                        key={time.unix() + 'display value'}
                                                        className={'sideBordered ' + addedClass}
                                                        bgColor={cellThreatLimit && cellThreatLimit.backgroundColor}
                                                        defaultColour={colour}
                                                    >
                                                        {cellText}
                                                    </ThreatCell>
                                                );
                                            })}
                                        </tr>
                                    );
                                });
                            })}
                        </tbody>
                    )}
                </table>
            </div>
            <DataTableThreatLegend threatLimits={threatLimits} fontSize={fontSize} />
        </DataTableDiv>
    );
};

export default DataTableHorizontal;
